'use client'

import React, { useState, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import { Box, Typography, IconButton, Link as MuiLink, Container } from '@mui/material'
import { SvgIconComponent } from '@mui/icons-material'
import Image from 'next/image'
import FlyinLogo from '@/src/assets/svgs/FlyinLogo'
import VatBadge from '@/src/components/common/footer/VatBadge'
import { TwitterIcon } from '@/src/components/common/icons/TwitterIcon'
import { ThreadsIcon } from '@/src/components/common/icons/ThreadsIcon'
import isGlobalDomain from '@/src/utils/generalUtils/isGlobalDomain'

import TopDestinationsWrapper from '@/src/components/common/home/topDestinations'
import TopAirlinesWrapper from '@/src/components/common/home/topAirlines'
import HomeHighlights from '@/src/components/common/home/HomeHighlights'

interface FooterLink {
    label: string
    href: string
}

interface SocialMediaLink {
    name: string
    href: string
    icon: SvgIconComponent | string
}

interface HomepageFooterProps {
    companyLinks: FooterLink[]
    supportLinks: FooterLink[]
    socialMediaLinks: SocialMediaLink[]
    isSignedIn?: boolean
}

const HomepageFooter: React.FC<HomepageFooterProps> = ({
    companyLinks,
    supportLinks,
    socialMediaLinks,
    isSignedIn = false
}) => {
    const t = useTranslations('footer')
    const [showTravelLicense, setShowTravelLicense] = useState(false)

    useEffect(() => {
        setShowTravelLicense(isGlobalDomain())
    }, [])

    return (
        <>
            {/* Highlights */}
            <HomeHighlights isMobile={false} />

            {/* Top Destinations and Top Airlines Section */}
            <Box sx={{ bgcolor: 'grey.50', py: 3 }}>
                <Container maxWidth="xl" disableGutters>
                    {/* Top Destinations Section */}
                    <TopDestinationsWrapper />
                    <Box sx={{ height: '1px', bgcolor: 'divider', m: 2 }} />

                    {/* Top Airlines Section */}
                    <TopAirlinesWrapper />
                </Container>
            </Box>

            {/* Main Footer */}
            <Box sx={{ bgcolor: 'grey.100', pt: 4, pb: 3 }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                    {/* Top Section: Logo + License Info + Social Icons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            pb: 3
                        }}
                    >
                        {/* Left: Logo and License Info */}
                        <Box>
                            {/* Logo */}
                            <Box sx={{ mb: 2 }}>
                                <FlyinLogo sx={{ width: 100, height: 42 }} />
                            </Box>

                            {/* License Information */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {showTravelLicense && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: '13px',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        {t('travelLicense')}:{' '}
                                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                            73100559
                                        </Box>
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '13px',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {t('licenseCategory')}:{' '}
                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        {t('travelAndTourismAgency')}
                                    </Box>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.primary',
                                        fontSize: '13px',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {t('registrationNumber')}:{' '}
                                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                        7001623003
                                    </Box>
                                </Typography>
                            </Box>
                        </Box>

                        {/* Right: Social Media Icons */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                {socialMediaLinks.map(social => (
                                    <IconButton
                                        key={social.name}
                                        href={social.href}
                                        target="_blank"
                                        sx={{
                                            bgcolor: 'common.white',
                                            width: 48,
                                            height: 48,
                                            borderRadius: '12%',
                                            '&:hover': { bgcolor: 'grey.200', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
                                        }}
                                    >
                                        {social.icon === 'twitter' ? (
                                            <TwitterIcon sx={{ color: 'text.primary', fontSize: 24 }} />
                                        ) : social.icon === 'threads' ? (
                                            <ThreadsIcon sx={{ color: 'text.primary', fontSize: 24 }} />
                                        ) : (
                                            React.createElement(social.icon as SvgIconComponent, {
                                                sx: { color: 'text.primary', fontSize: 24 }
                                            })
                                        )}
                                    </IconButton>
                                ))}
                            </Box>
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 348,
                                    borderRadius: 1,
                                    p: 1,
                                    bgcolor: 'grey.300',
                                    display: { xs: 'none', md: 'block' },
                                    overflow: 'hidden'
                                }}
                            >
                                <Image
                                    src="https://ui.cltp.co/flyinstatic/common/themes/v2/img/support-cards.png"
                                    alt={t('support')}
                                    width={348}
                                    height={48}
                                    loading="lazy"
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ height: '1px', bgcolor: 'divider', my: 3 }} />

                    {/* Bottom Section: Links + VAT + Copyright */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        {/* Left: Navigation Links */}
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            {companyLinks.map((link, index) => (
                                <MuiLink
                                    key={index}
                                    href={link.href}
                                    sx={{
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        '&:hover': { color: 'primary.main' }
                                    }}
                                >
                                    {link.label}
                                </MuiLink>
                            ))}
                            {supportLinks
                                .filter(link => link.label === t('fileClaim'))
                                .map((link, index) => (
                                    <MuiLink
                                        key={`file-claim-${index}`}
                                        href={link.href}
                                        sx={{
                                            color: 'text.secondary',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            textTransform: 'none',
                                            '&:hover': { color: 'primary.main' }
                                        }}
                                    >
                                        {link.label}
                                    </MuiLink>
                                ))}
                        </Box>

                        {/* Right: VAT Logo + Copyright */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <VatBadge size={40} />
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', fontSize: '13px', fontWeight: 400 }}
                            >
                                {t('copyright', { year: new Date().getFullYear() })}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    )
}

export { HomepageFooter }
export type { HomepageFooterProps }
